import React, { useState, useEffect, useRef } from "react"
import { Grid } from "@material-ui/core"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import { waitUntil } from "@/utils/time"
import { turnBilledDurationSecondsInCredits } from "@/utils/Voice"

import useAsyncState from "@/hooks/useAsyncState"
import useCounter from "@/hooks/useCounter"
import { ErrorType } from "@/hooks/useValidation"

import UpsellQuantityDialog from "@/components/UpsellDialog/UpsellQuantityDialog"

import { ZenviaWebphoneHandle } from "@/@types/zenvia"

export type CallState = "idle" | "loading" | "calling" | "active"

type ZenviaCallbackMessageStatus =
	| "conectado"
	| "chamando"
	| "conversando"
	| "atendida"
	| "encerrada"
	| "sem resposta"
	| "ocupado"
	| "falha"
	| "congestionado"

/**
 * NOTE: Field names in this type intentionally use the Zenvia wire-format
 * naming (snake_case / Portuguese) to match the postMessage payload exactly.
 * Do not rename them to camelCase — it would break runtime matching.
 */
type ZenviaCallbackMessageToZenviaPayload = {
	status: {
		status: ZenviaCallbackMessageStatus
		/** Zenvia wire-format field. Present only when the call was successfully connected to a real destination. */
		chamada_id?: string
	}
	stats_webphone: {
		internet: string
	}
}

type ZenviaCallbackMessage = keyof ZenviaCallbackMessageToZenviaPayload

type ZenviaCallbackPayload<Message extends ZenviaCallbackMessage> = ZenviaCallbackMessageToZenviaPayload[Message]

type ZenviaActionMessageToPayload = {
	chamaNumero: {
		numero: string
	}
	desligaChamada: Record<string, never>
}

type ZenviaActionMessage = keyof ZenviaActionMessageToPayload

type ZenviaActionPayload<ActionMessage extends ZenviaActionMessage> = ZenviaActionMessageToPayload[ActionMessage]

type FormatPhoneNumberResponse = {
	formattedPhoneNumber: string
	fallbackFormattedPhoneNumbers?: string[]
}

const WEBPHONE_WIDGET_CONTAINER_ID = "widget-evoline-api-container"
const WEBPHONE_SCRIPT_ID = "webphone-script"

type UseVoiceCallReturn = {
	callState: CallState
	elapsedSeconds: number
	formattedElapsedSeconds: string
	billedCredits: number
	startCall: (phoneNumber: string) => Promise<void>
	endCall: () => void
	loadWebphone: () => Promise<unknown>
	webphoneContainer: React.ReactElement
}

type UseVoiceCallInput = {
	instanceId: number
	mustBlock: boolean
	isLoading: boolean
}

const useVoiceCall = (input: UseVoiceCallInput): UseVoiceCallReturn => {
	const {
		instanceId,
		isLoading,
		mustBlock
	} = input

	const [isWebphoneReadyForCallings, setIsWebphoneReadyForCallings] = useAsyncState(false)
	const [isLoadingWebphone, setIsLoadingWebphone] = useAsyncState(false)
	const [callState, setCallState] = useState<CallState>("idle")

	const webphoneRef = useRef<ZenviaWebphoneHandle | null>(null)
	const fallbackFormattedPhoneNumbersRef = useRef<string[]>([])
	const fallbackIndexRef = useRef(0)

	const callCounter = useCounter({ direction: "up", startFromSeconds: 0 })

	const handleSendToWebphone = <ActionMessage extends ZenviaActionMessage>(actionMessage: ActionMessage, payload: ZenviaActionPayload<ActionMessage>) => {
		const webphone = webphoneRef.current

		if (!webphone) {
			return
		}

		if (webphone.contentWindow) {
			webphone.contentWindow.postMessage({ ...payload, message: actionMessage }, "*")
		} else if (typeof webphone.postMessage === "function") {
			webphone.postMessage({ ...payload, message: actionMessage }, "*")
		}
	}

	const clearWebphoneWidgetContainer = () => {
		const existingWebphoneWidgetContainer = document.getElementById(WEBPHONE_WIDGET_CONTAINER_ID)

		if (existingWebphoneWidgetContainer) {
			existingWebphoneWidgetContainer.innerHTML = ""
		}
	}

	const endCall = () => {
		if (callState === "active") {
			handleSendToWebphone("desligaChamada", {})
		}

		callCounter.reset()
		fallbackIndexRef.current = 0
		fallbackFormattedPhoneNumbersRef.current = []
		setIsWebphoneReadyForCallings(() => false)
		setCallState("idle")
		clearWebphoneWidgetContainer()
	}

	const loadWebphone = async () => {
		try {
			// eslint-disable-next-line no-async-promise-executor
			await new Promise(async (resolve, reject) => {
				try {
					if (isWebphoneReadyForCallings.current) {
						return resolve(null)
					}

					if (isLoadingWebphone.current) {
						return resolve(await waitUntil(() => isWebphoneReadyForCallings.current))
					} else {
						setIsLoadingWebphone(() => true)
					}

					const response = await ApiService.get<{ url: string }>("/voice/webphone")

					await new Promise((resolve, reject) => {
						const existingWebphoneScript = document.getElementById(WEBPHONE_SCRIPT_ID)

						if (existingWebphoneScript) {
							existingWebphoneScript.remove()
						}

						const webphoneScript = document.createElement("script")

						webphoneScript.onload = () => resolve(null)
						webphoneScript.onerror = (error) => reject(error)

						webphoneScript.id = WEBPHONE_SCRIPT_ID
						webphoneScript.src = response.data.url
						document.body.appendChild(webphoneScript)
					})

					const iframeWebphoneHandle = document.getElementById("webphone") as HTMLIFrameElement
					const globalWebphoneHandle = window.webphone
					webphoneRef.current = iframeWebphoneHandle ?? globalWebphoneHandle

					return resolve(await waitUntil(() => isWebphoneReadyForCallings.current))
				} catch (error) {
					return reject(error)
				} finally {
					setIsLoadingWebphone(() => false)
				}
			})

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const handleRetryCall = async (retryPhoneNumber: string) => {
		callCounter.reset()
		setIsWebphoneReadyForCallings(() => false)
		setCallState("loading")
		clearWebphoneWidgetContainer()

		const isWebphoneLoaded = await loadWebphone()

		if (!isWebphoneLoaded) {
			return
		}

		handleSendToWebphone("chamaNumero", { numero: retryPhoneNumber })
	}

	const handleZenviaStatusMessage = (data: ZenviaCallbackPayload<"status">) => {
		const zenviaStatusToHandlerFn: Record<ZenviaCallbackMessageStatus, () => void> = {
			conectado: () => setIsWebphoneReadyForCallings(() => true),
			chamando: () => {
				setCallState("calling")
			},
			/**
			 * WARNING:
			 * - Zenvia fires "conversando" even when the call reaches a dead variant (e.g. the
			 * number with the extra 9th digit when the real number has 8 digits). In that case,
			 * chamada_id is absent. We use the missing ID as a signal to hang up and redial with
			 * the fallback number before surfacing the call as active to the user.
			 */
			conversando: () => {
				const hasCallId = Boolean(data.chamada_id)
				const hasRemainingFallbackCandidate = fallbackIndexRef.current < fallbackFormattedPhoneNumbersRef.current.length
				const canRetryWithFallback = !hasCallId && hasRemainingFallbackCandidate

				if (canRetryWithFallback) {
					const fallbackCandidate = fallbackFormattedPhoneNumbersRef.current[fallbackIndexRef.current]
					fallbackIndexRef.current += 1
					handleRetryCall(fallbackCandidate)
				} else {
					setCallState("active")
					callCounter.start()
				}
			},
			atendida: () => null,
			encerrada: () => endCall(),
			"sem resposta": () => endCall(),
			ocupado: () => endCall(),
			falha: () => endCall(),
			congestionado: () => endCall()
		}

		const handlerFn = zenviaStatusToHandlerFn[data.status]

		if (handlerFn) {
			handlerFn()
		}
	}

	const handleZenviaCallbackMessage = (message: ZenviaCallbackMessage, data: ZenviaCallbackPayload<ZenviaCallbackMessage>) => {
		const zenviaCallbackMessageToHandlerFn: Record<ZenviaCallbackMessage, (data: ZenviaCallbackPayload<ZenviaCallbackMessage>) => void> = {
			status: (data) => handleZenviaStatusMessage(data as ZenviaCallbackPayload<"status">),
			stats_webphone: () => null
		}

		const handlerFn = zenviaCallbackMessageToHandlerFn[message]

		if (handlerFn) {
			handlerFn(data)
		}
	}

	const startCall = async (phoneNumber: string) => {
		try {
			setCallState("loading")

			await waitUntil(() => !isLoading)

			if (mustBlock) {
				UpsellQuantityDialog.open({
					type: "voice_call_minutes",
					isUserReachSubscriptionLimit: true,
					instanceId
				})

				setCallState("idle")

				return
			}

			const isWebphoneLoaded = await loadWebphone()

			if (!isWebphoneLoaded) {
				return
			}

			const formatResponse = await ApiService.get<FormatPhoneNumberResponse>("/voice/phone-number/format", {
				params: {
					phoneNumber
				}
			})

			fallbackFormattedPhoneNumbersRef.current = formatResponse.data.fallbackFormattedPhoneNumbers ?? []
			fallbackIndexRef.current = 0
			handleSendToWebphone("chamaNumero", { numero: formatResponse.data.formattedPhoneNumber })
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	useEffect(() => {
		const onZenviaMessageCallback = (event: MessageEvent<{ message: ZenviaCallbackMessage } & ZenviaCallbackPayload<ZenviaCallbackMessage>>) => {
			const data = event.data

			if (!data?.message) {
				return
			}

			handleZenviaCallbackMessage(data.message, data)
		}

		window.addEventListener("message", onZenviaMessageCallback)

		return () => window.removeEventListener("message", onZenviaMessageCallback)
	}, [])

	const billedCredits = turnBilledDurationSecondsInCredits(callCounter.elapsedSeconds)

	const webphoneContainer = (
		<Grid id={WEBPHONE_WIDGET_CONTAINER_ID} style={{ display: "none" }} />
	)

	return {
		callState,
		elapsedSeconds: callCounter.elapsedSeconds,
		formattedElapsedSeconds: callCounter.formattedElapsedSeconds,
		billedCredits,
		startCall,
		endCall,
		loadWebphone,
		webphoneContainer
	}
}

export default useVoiceCall
